import React, { useEffect, useMemo, useState } from "react";
import TablePagination from "../../components/shared/TablePagination";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { Modal } from "react-bootstrap";
import { getApiErrorMessage } from "../../store/api/baseApi";
import { exportToExcel } from "../../utils/exportExcel";
import {
  ClientRecord,
  useCreateClientMutation,
  useDeleteClientMutation,
  useFetchClientPermissionsQuery,
  useFetchClientsQuery,
  useUpdateClientMutation,
  useUpdateClientPermissionsMutation,
} from "../../store/api/clientsApi";
import { useFetchClientProjectsForAdminQuery, useFetchProjectsQuery } from "../../store/api/projectsApi";
import "./admin.css";

// ─── Types ───────────────────────────────────────────────────────────────────

type ActiveModal =
  | { kind: "create" }
  | { kind: "edit"; client: ClientRecord }
  | { kind: "permissions"; client: ClientRecord }
  | { kind: "projects"; client: ClientRecord }
  | { kind: "credential"; client: ClientRecord; password: string }
  | null;

type CreateForm = {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
  city: string;
};

type EditForm = {
  name: string;
  phone: string;
  city: string;
  isActive: boolean;
};

const EMPTY_CREATE: CreateForm = {
  name: "",
  email: "",
  password: "",
  confirmPassword: "",
  phone: "",
  city: "",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const generatePassword = (): string => {
  const charset = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$";
  return Array.from({ length: 12 }, () =>
    charset[Math.floor(Math.random() * charset.length)]
  ).join("");
};

const validateCreate = (form: CreateForm): string => {
  if (!form.name.trim()) return "Full name is required.";
  if (!form.email.trim()) return "Email address is required.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
    return "Enter a valid email address.";
  if (!form.password) return "Password is required.";
  if (form.password.length < 8) return "Password must be at least 8 characters.";
  if (form.password !== form.confirmPassword) return "Passwords do not match.";
  return "";
};

const validateEdit = (form: EditForm): string => {
  if (!form.name.trim()) return "Full name is required.";
  return "";
};

// ─── Client Projects (read-only admin view) ───────────────────────────────────

const statusColor = (s: string) =>
  s === "completed" ? "#15803d" : s === "in-progress" ? "#2563eb" : "#64748b";
const statusBg = (s: string) =>
  s === "completed" ? "#dcfce7" : s === "in-progress" ? "#dbeafe" : "#f1f5f9";

const ClientProjectsPanel: React.FC<{
  client: ClientRecord;
  onClose: () => void;
}> = ({ client, onClose }) => {
  const { data: projects = [], isLoading, error } = useFetchClientProjectsForAdminQuery(client.id);

  const loadError = error ? getApiErrorMessage(error as FetchBaseQueryError) : "";

  const avgProgress = (tasks: { actualPercent: number }[]) =>
    tasks.length ? Math.round(tasks.reduce((s, t) => s + t.actualPercent, 0) / tasks.length) : 0;

  return (
    <>
      <Modal.Header closeButton closeVariant="white" style={{ background: "linear-gradient(135deg, #1e3a5f, #2563eb, #3b82f6)", border: "none", padding: "10px 20px" }}>
        <Modal.Title style={{ color: "#fff", fontWeight: 700, fontSize: "0.92rem", display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="bi bi-folder2-open" style={{ color: "#fff", fontSize: 13 }} />
          </div>
          Assigned Projects — {client.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "65vh", overflowY: "auto" }}>
        {loadError && <div className="alert alert-danger py-2 mb-3">{loadError}</div>}
        {isLoading ? (
          <div className="alert alert-light border">Loading projects…</div>
        ) : projects.length === 0 ? (
          <div className="text-center py-4">
            <i className="bi bi-folder-x" style={{ fontSize: "2.5rem", color: "#94a3b8" }} />
            <p className="text-muted mt-2 mb-0">No projects have been assigned to this client yet.</p>
            <p className="text-muted small">Use the <i className="bi bi-shield-lock" /> Permissions button to assign projects.</p>
          </div>
        ) : (
          <>
            <p className="text-muted small mb-3">
              {projects.length} project{projects.length !== 1 ? "s" : ""} visible to <strong>{client.name}</strong>.
            </p>
            <div className="d-flex flex-column gap-3">
              {projects.map((project) => {
                const progress = avgProgress(project.progressTasks);
                const taskCount = project.progressTasks.length;
                const delayed = project.progressTasks.filter((t) => {
                  const health = t.actualPercent >= 100 ? "completed"
                    : new Date(t.plannedEndDate) < new Date() ? "delayed" : "on-track";
                  return health === "delayed";
                }).length;
                return (
                  <div
                    key={project.id}
                    style={{ border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "14px 16px", background: "#fafbfc" }}
                  >
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                      <div>
                        <div style={{ fontWeight: 700, fontSize: "0.92rem", color: "#0f172a" }}>{project.title}</div>
                        {project.location && (
                          <div className="small text-muted mt-1">
                            <i className="bi bi-geo-alt me-1" />{project.location}
                          </div>
                        )}
                      </div>
                      <span
                        style={{
                          background: statusBg(project.status),
                          color: statusColor(project.status),
                          borderRadius: 20,
                          padding: "2px 10px",
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        {project.status}
                      </span>
                    </div>
                    <div className="d-flex gap-3 small text-muted mb-2">
                      <span><i className="bi bi-list-task me-1" />{taskCount} task{taskCount !== 1 ? "s" : ""}</span>
                      {delayed > 0 && (
                        <span style={{ color: "#dc2626" }}>
                          <i className="bi bi-exclamation-triangle me-1" />{delayed} delayed
                        </span>
                      )}
                      {project.contractFiles.length > 0 && (
                        <span><i className="bi bi-paperclip me-1" />{project.contractFiles.length} file{project.contractFiles.length !== 1 ? "s" : ""}</span>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <div className="progress flex-grow-1" style={{ height: 6, borderRadius: 4, background: "#e2e8f0" }}>
                        <div
                          className="progress-bar"
                          style={{
                            width: `${progress}%`,
                            background: progress >= 100 ? "#15803d" : progress > 50 ? "#2563eb" : "#f59e0b",
                            borderRadius: 4,
                          }}
                        />
                      </div>
                      <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#475569", minWidth: 36, textAlign: "right" }}>
                        {progress}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer style={{ borderTop: "1.5px dashed #e2e8f0", background: "#f8fafc" }}>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
          Close
        </button>
      </Modal.Footer>
    </>
  );
};

// ─── Permissions sub-component ───────────────────────────────────────────────

const PermissionsPanel: React.FC<{
  client: ClientRecord;
  onClose: () => void;
  onSaved: (msg: string) => void;
}> = ({ client, onClose, onSaved }) => {
  const { data: allProjects = [], isLoading: loadingProjects, error: projectsError } = useFetchProjectsQuery();
  const {
    data: assignedIds = [],
    isLoading: loadingPerms,
    error: permsError,
  } = useFetchClientPermissionsQuery(client.id);
  const [updatePermissions, { isLoading: isSaving }] = useUpdateClientPermissionsMutation();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    setSelected(new Set(assignedIds));
  }, [assignedIds]);

  const toggle = (projectId: string) => {
    setSaveError("");
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    setSaveError("");
    if (selected.size === allProjects.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allProjects.map((p) => p.id)));
    }
  };

  const handleSave = async () => {
    setSaveError("");
    try {
      await updatePermissions({
        clientId: client.id,
        projectIds: Array.from(selected),
      }).unwrap();
      onSaved(`Access permissions updated for ${client.name}.`);
      onClose();
    } catch (err) {
      setSaveError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  const isLoading = loadingProjects || loadingPerms;
  const loadError = projectsError
    ? getApiErrorMessage(projectsError as FetchBaseQueryError)
    : permsError
    ? getApiErrorMessage(permsError as FetchBaseQueryError)
    : "";

  return (
    <>
      <Modal.Header closeButton closeVariant="white" style={{ background: "linear-gradient(135deg, #134e4a, #0d9488, #14b8a6)", border: "none", padding: "10px 20px" }}>
        <Modal.Title style={{ color: "#fff", fontWeight: 700, fontSize: "0.92rem", display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="bi bi-shield-lock-fill" style={{ color: "#fff", fontSize: 13 }} />
          </div>
          Access Permissions — {client.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          Select which projects this client can view after logging in. Changes take effect immediately after saving.
        </p>
        {loadError && <div className="alert alert-danger py-2 mb-3">{loadError}</div>}
        {saveError && <div className="alert alert-danger py-2 mb-3">{saveError}</div>}
        {isLoading ? (
          <div className="alert alert-light border">Loading projects…</div>
        ) : allProjects.length === 0 ? (
          <div className="alert alert-light border">
            No projects exist yet. Create a project first to assign access.
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="fw-semibold small text-muted">
                {selected.size} of {allProjects.length} project{allProjects.length !== 1 ? "s" : ""} selected
              </span>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-decoration-none"
                onClick={toggleAll}
              >
                {selected.size === allProjects.length ? "Deselect all" : "Select all"}
              </button>
            </div>
            <div className="client-perm-list">
              {allProjects.map((project) => (
                <label
                  key={project.id}
                  className={`client-perm-item d-flex align-items-center gap-3${selected.has(project.id) ? " active" : ""}`}
                >
                  <input
                    type="checkbox"
                    className="form-check-input m-0 flex-shrink-0"
                    checked={selected.has(project.id)}
                    onChange={() => toggle(project.id)}
                  />
                  <div className="flex-grow-1 min-w-0">
                    <div className="fw-semibold text-truncate">{project.title}</div>
                    <div className="small text-muted text-truncate">
                      {project.location || "Location pending"} &bull;{" "}
                      <span
                        className={`badge rounded-pill ${
                          project.status === "completed"
                            ? "text-bg-success"
                            : project.status === "in-progress"
                            ? "text-bg-primary"
                            : "text-bg-secondary"
                        }`}
                      >
                        {project.status}
                      </span>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer style={{ borderTop: "1.5px dashed #e2e8f0", background: "#f8fafc" }}>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={isSaving}>
          Cancel
        </button>
        <button
          type="button"
          disabled={isSaving || isLoading}
          style={{ background: "linear-gradient(135deg, #0d9488, #14b8a6)", border: "none", color: "#fff", borderRadius: 8, padding: "7px 18px", fontSize: "0.83rem", fontWeight: 700, cursor: isSaving ? "wait" : "pointer", display: "inline-flex", alignItems: "center", gap: 7, boxShadow: "0 3px 10px #0d948833" }}
          onClick={handleSave}
        >
          {isSaving
            ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> Saving…</>
            : <><i className="bi bi-shield-check" /> Save Permissions</>}
        </button>
      </Modal.Footer>
    </>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const ClientManager: React.FC = () => {
  const { data: clients = [], isLoading, error } = useFetchClientsQuery();
  const [createClient, { isLoading: isCreating }] = useCreateClientMutation();
  const [updateClient, { isLoading: isUpdating }] = useUpdateClientMutation();
  const [deleteClient, { isLoading: isDeleting }] = useDeleteClientMutation();

  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;
  const [createForm, setCreateForm] = useState<CreateForm>(EMPTY_CREATE);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [formError, setFormError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const errorMessage = error ? getApiErrorMessage(error as FetchBaseQueryError) : "";

  const filteredClients = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return clients.filter((c) => {
      const matchStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && c.isActive) ||
        (statusFilter === "inactive" && !c.isActive);
      const matchSearch =
        !q ||
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.phone.toLowerCase().includes(q) ||
        c.city.toLowerCase().includes(q);
      return matchStatus && matchSearch;
    });
  }, [clients, searchQuery, statusFilter]);

  const activeCount = clients.filter((c) => c.isActive).length;
  const inactiveCount = clients.length - activeCount;

  useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredClients.length / PAGE_SIZE));
  const pagedClients = filteredClients.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const isBusy = isCreating || isUpdating || isDeleting;

  // ── Create ────────────────────────────────────────────────────────────────

  const openCreateModal = () => {
    setCreateForm({ ...EMPTY_CREATE, password: generatePassword() });
    setFormError("");
    setActionError("");
    setActionMessage("");
    setShowPassword(false);
    setActiveModal({ kind: "create" });
  };

  const handleCreateField = (field: keyof CreateForm, value: string) => {
    setCreateForm((prev) => ({ ...prev, [field]: value }));
    setFormError("");
  };

  const handleCreate = async () => {
    const validationError = validateCreate(createForm);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setFormError("");
    try {
      await createClient({
        name: createForm.name.trim(),
        email: createForm.email.trim().toLowerCase(),
        password: createForm.password,
        phone: createForm.phone.trim(),
        city: createForm.city.trim(),
      }).unwrap();
      const savedPassword = createForm.password;
      const savedClient = { ...createForm } as unknown as ClientRecord;
      setActiveModal({ kind: "credential", client: savedClient, password: savedPassword });
      setActionMessage(`Client account created for ${createForm.name}.`);
    } catch (err) {
      setFormError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  // ── Edit ──────────────────────────────────────────────────────────────────

  const openEditModal = (client: ClientRecord) => {
    setEditForm({
      name: client.name,
      phone: client.phone,
      city: client.city,
      isActive: client.isActive,
    });
    setFormError("");
    setActionError("");
    setActionMessage("");
    setActiveModal({ kind: "edit", client });
  };

  const handleEditField = (field: keyof EditForm, value: string | boolean) => {
    setEditForm((prev) => (prev ? { ...prev, [field]: value } : prev));
    setFormError("");
  };

  const handleSaveEdit = async () => {
    if (activeModal?.kind !== "edit" || !editForm) return;
    const validationError = validateEdit(editForm);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setFormError("");
    try {
      await updateClient({ id: activeModal.client.id, payload: editForm }).unwrap();
      setActionMessage(`Client "${editForm.name}" updated.`);
      setActiveModal(null);
    } catch (err) {
      setFormError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = async (client: ClientRecord) => {
    if (!window.confirm(`Delete client "${client.name}"? This cannot be undone.`)) return;
    setActionError("");
    setActionMessage("");
    try {
      await deleteClient(client.id).unwrap();
      setActionMessage(`Client "${client.name}" deleted.`);
    } catch (err) {
      setActionError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <section className="admin-page">
      <div className="container-fluid px-0">
        <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px #0000000a" }}>
          {/* Gradient header */}
          <div style={{ background: "linear-gradient(135deg, #134e4a, #0d9488, #14b8a6)", padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className="bi bi-people-fill" style={{ color: "#fff", fontSize: 14 }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#fff", lineHeight: 1.2 }}>Client Access Management</div>
                <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)" }}>Create client logins and control project access permissions</div>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
              <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem", fontWeight: 600 }}>{clients.length} Total</span>
              <span style={{ background: "#dcfce7", color: "#15803d", borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem", fontWeight: 700 }}>{activeCount} Active</span>
              {inactiveCount > 0 && <span style={{ background: "#f1f5f9", color: "#64748b", borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem", fontWeight: 700 }}>{inactiveCount} Inactive</span>}
              <button type="button" style={{ background: "rgba(255,255,255,0.9)", color: "#0d9488", border: "none", borderRadius: 8, padding: "5px 14px", fontSize: "0.82rem", fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }} onClick={openCreateModal}>
                <i className="bi bi-person-plus-fill" /> Create Client
              </button>
            </div>
          </div>
          {/* Filter bar */}
          <div className="d-flex flex-wrap gap-3 align-items-center p-3 border-bottom">
            <div style={{ minWidth: 240 }}>
              <input
                className="form-control form-control-sm"
                placeholder="Search by name, email, phone, or city…"
                title="Search clients"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div style={{ minWidth: 160 }}>
              <select
                className="form-select form-select-sm"
                title="Filter by status"
                aria-label="Filter by status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as "all" | "active" | "inactive")}
              >
                <option value="all">All Clients</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>
            <button type="button" className="btn btn-outline-success btn-sm d-inline-flex align-items-center gap-2"
              onClick={() => exportToExcel(filteredClients.map((c) => ({ Name: c.name, Email: c.email, Phone: c.phone, City: c.city, Role: c.role, Status: c.isActive ? "Active" : "Inactive", "Created At": c.createdAt })), "Clients")}>
              <i className="bi bi-file-earmark-excel" /> Export
            </button>
            <span className="small text-muted ms-auto">
              {filteredClients.length} client{filteredClients.length !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="card-body p-4">

            {/* Alerts */}
            {isLoading && <div className="alert alert-light border">Loading clients…</div>}
            {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
            {actionError && <div className="alert alert-danger">{actionError}</div>}
            {actionMessage && <div className="alert alert-success">{actionMessage}</div>}

            {/* Info banner */}
            {!isLoading && !errorMessage && clients.length === 0 && (
              <div className="client-manager-empty p-5 text-center rounded-4 border border-dashed mb-2">
                <i className="bi bi-people fs-1 text-muted d-block mb-3"></i>
                <h3 className="h5 mb-2">No client accounts yet</h3>
                <p className="text-muted mb-3">
                  Click <strong>Create Client</strong> to generate login credentials and grant portal access.
                </p>
                <button
                  type="button"
                  className="btn btn-primary d-inline-flex align-items-center gap-2"
                  onClick={openCreateModal}
                >
                  <i className="bi bi-person-plus-fill"></i>
                  <span>Create First Client</span>
                </button>
              </div>
            )}

            {/* Desktop table */}
            {!isLoading && filteredClients.length > 0 && (
              <>
                <div className="request-table-shell d-none d-lg-block">
                  <div className="table-responsive">
                    <table className="table align-middle mb-0 admin-request-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Client</th>
                          <th>Contact</th>
                          <th>City</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedClients.map((client, idx) => (
                          <tr key={client.id}>
                            <td className="text-muted">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                            <td>
                              <div className="request-primary">{client.name}</div>
                              <div className="request-meta-line">{client.email}</div>
                              <div className="request-meta-line">Role: {client.role}</div>
                            </td>
                            <td>
                              <div>{client.phone || <span className="text-muted">—</span>}</div>
                            </td>
                            <td>
                              <div>{client.city || <span className="text-muted">—</span>}</div>
                            </td>
                            <td>
                              <span
                                className={`badge rounded-pill px-3 py-2 ${
                                  client.isActive ? "text-bg-success" : "text-bg-secondary"
                                }`}
                              >
                                {client.isActive ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td>
                              <span className="request-meta-line">{client.createdAt || "—"}</span>
                            </td>
                            <td>
                              <div className="admin-table-actions">
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm admin-icon-btn"
                                  title="Edit client"
                                  aria-label={`Edit ${client.name}`}
                                  onClick={() => openEditModal(client)}
                                  disabled={isBusy}
                                >
                                  <i className="bi bi-pencil-square"></i>
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-outline-info btn-sm admin-icon-btn"
                                  title="View assigned projects"
                                  aria-label={`View projects for ${client.name}`}
                                  onClick={() => setActiveModal({ kind: "projects", client })}
                                  disabled={isBusy}
                                >
                                  <i className="bi bi-folder2-open"></i>
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm admin-icon-btn"
                                  title="Manage project permissions"
                                  aria-label={`Permissions for ${client.name}`}
                                  onClick={() => setActiveModal({ kind: "permissions", client })}
                                  disabled={isBusy}
                                >
                                  <i className="bi bi-shield-lock"></i>
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm admin-icon-btn"
                                  title="Delete client"
                                  aria-label={`Delete ${client.name}`}
                                  onClick={() => handleDelete(client)}
                                  disabled={isBusy}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile cards */}
                <div className="d-grid gap-3 d-lg-none">
                  {pagedClients.map((client) => (
                    <article key={client.id} className="card border-0 shadow-sm rounded-4">
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                          <div>
                            <h3 className="h6 mb-1">{client.name}</h3>
                            <div className="small text-muted">{client.email}</div>
                          </div>
                          <span
                            className={`badge rounded-pill px-3 py-2 ${
                              client.isActive ? "text-bg-success" : "text-bg-secondary"
                            }`}
                          >
                            {client.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                        <div className="row g-2 small mb-3">
                          <div className="col-6">
                            <div className="text-muted">Phone</div>
                            <div>{client.phone || "—"}</div>
                          </div>
                          <div className="col-6">
                            <div className="text-muted">City</div>
                            <div>{client.city || "—"}</div>
                          </div>
                          <div className="col-6">
                            <div className="text-muted">Role</div>
                            <div>{client.role}</div>
                          </div>
                          <div className="col-6">
                            <div className="text-muted">Created</div>
                            <div>{client.createdAt || "—"}</div>
                          </div>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                            onClick={() => openEditModal(client)}
                            disabled={isBusy}
                          >
                            <i className="bi bi-pencil-square"></i> Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-info btn-sm d-inline-flex align-items-center gap-1"
                            onClick={() => setActiveModal({ kind: "projects", client })}
                            disabled={isBusy}
                          >
                            <i className="bi bi-folder2-open"></i> Projects
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                            onClick={() => setActiveModal({ kind: "permissions", client })}
                            disabled={isBusy}
                          >
                            <i className="bi bi-shield-lock"></i> Permissions
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1"
                            onClick={() => handleDelete(client)}
                            disabled={isBusy}
                          >
                            <i className="bi bi-trash"></i> Delete
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </>
            )}

            {!isLoading && clients.length > 0 && filteredClients.length === 0 && (
              <div className="alert alert-light border mt-3 mb-0">
                No clients match the current filters.
              </div>
            )}
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredClients.length}
              pageSize={PAGE_SIZE}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </div>

      {/* ── Create Client Modal ─────────────────────────────────────────────── */}
      <Modal
        show={activeModal?.kind === "create"}
        onHide={() => setActiveModal(null)}
        centered
        size="lg"
      >
        <Modal.Header closeButton closeVariant="white" style={{ background: "linear-gradient(135deg, #134e4a, #0d9488, #14b8a6)", border: "none", padding: "10px 20px" }}>
          <Modal.Title style={{ color: "#fff", fontWeight: 700, fontSize: "0.92rem", display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 7, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className="bi bi-person-plus-fill" style={{ color: "#fff", fontSize: 13 }} />
            </div>
            Create Client Account
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Fill in the details below. The client will use the email and password to log in to their portal at <strong>/client/login</strong>.
          </p>
          {formError && <div className="alert alert-danger py-2 mb-3">{formError}</div>}
          <div className="row g-3 align-items-end">
            <div className="col-md-6">
              <div style={{ borderLeft: "3px solid #6366f1", paddingLeft: 10 }}>
                <label className="form-label mb-1 fw-semibold" style={{ fontSize: "0.84rem", color: "#334155" }}>
                  Full Name <span className="text-danger">*</span>
                  <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Client's full display name</small>
                </label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text" style={{ background: "#eef2ff", borderColor: "#c7d2fe", color: "#6366f1" }}><i className="bi bi-person-fill" style={{ fontSize: 12 }} /></span>
                  <input className="form-control form-control-sm" style={{ borderColor: "#c7d2fe", boxShadow: "none" }} title="Client full name" value={createForm.name} onChange={(e) => handleCreateField("name", e.target.value)} />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div style={{ borderLeft: "3px solid #f59e0b", paddingLeft: 10 }}>
                <label className="form-label mb-1 fw-semibold" style={{ fontSize: "0.84rem", color: "#334155" }}>
                  Email Address <span className="text-danger">*</span>
                  <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Used to log in to the client portal</small>
                </label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text" style={{ background: "#fef3c7", borderColor: "#fcd34d", color: "#d97706" }}><i className="bi bi-envelope-fill" style={{ fontSize: 12 }} /></span>
                  <input type="email" className="form-control form-control-sm" style={{ borderColor: "#fcd34d", boxShadow: "none" }} title="Client email address" value={createForm.email} onChange={(e) => handleCreateField("email", e.target.value)} />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div style={{ borderLeft: "3px solid #8b5cf6", paddingLeft: 10 }}>
                <label className="form-label mb-1 fw-semibold" style={{ fontSize: "0.84rem", color: "#334155" }}>
                  Password <span className="text-danger">*</span>
                  <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Min. 8 characters — click ↻ to auto-generate</small>
                </label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text" style={{ background: "#ede9fe", borderColor: "#ddd6fe", color: "#7c3aed" }}><i className="bi bi-lock-fill" style={{ fontSize: 12 }} /></span>
                  <input type={showPassword ? "text" : "password"} className="form-control form-control-sm" style={{ borderColor: "#ddd6fe", boxShadow: "none" }} title="Client password" value={createForm.password} onChange={(e) => handleCreateField("password", e.target.value)} />
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowPassword((v) => !v)} title={showPassword ? "Hide" : "Show"}><i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`} /></button>
                  <button type="button" className="btn btn-outline-secondary btn-sm" title="Generate strong password" onClick={() => handleCreateField("password", generatePassword())}><i className="bi bi-arrow-repeat" /></button>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div style={{ borderLeft: "3px solid #8b5cf6", paddingLeft: 10 }}>
                <label className="form-label mb-1 fw-semibold" style={{ fontSize: "0.84rem", color: "#334155" }}>
                  Confirm Password <span className="text-danger">*</span>
                </label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text" style={{ background: "#ede9fe", borderColor: "#ddd6fe", color: "#7c3aed" }}><i className="bi bi-shield-lock-fill" style={{ fontSize: 12 }} /></span>
                  <input type={showPassword ? "text" : "password"} className="form-control form-control-sm" style={{ borderColor: "#ddd6fe", boxShadow: "none" }} title="Confirm client password" value={createForm.confirmPassword} onChange={(e) => handleCreateField("confirmPassword", e.target.value)} />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div style={{ borderLeft: "3px solid #10b981", paddingLeft: 10 }}>
                <label className="form-label mb-1 fw-semibold" style={{ fontSize: "0.84rem", color: "#334155" }}>
                  Phone
                  <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Contact number with country code (optional)</small>
                </label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text" style={{ background: "#d1fae5", borderColor: "#a7f3d0", color: "#059669" }}><i className="bi bi-telephone-fill" style={{ fontSize: 12 }} /></span>
                  <input type="tel" className="form-control form-control-sm" style={{ borderColor: "#a7f3d0", boxShadow: "none" }} title="Client phone number" value={createForm.phone} onChange={(e) => handleCreateField("phone", e.target.value)} />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div style={{ borderLeft: "3px solid #0ea5e9", paddingLeft: 10 }}>
                <label className="form-label mb-1 fw-semibold" style={{ fontSize: "0.84rem", color: "#334155" }}>
                  City
                  <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Client's city or project site location</small>
                </label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text" style={{ background: "#e0f2fe", borderColor: "#bae6fd", color: "#0ea5e9" }}><i className="bi bi-geo-alt-fill" style={{ fontSize: 12 }} /></span>
                  <input className="form-control form-control-sm" style={{ borderColor: "#bae6fd", boxShadow: "none" }} title="Client city" value={createForm.city} onChange={(e) => handleCreateField("city", e.target.value)} />
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1.5px dashed #e2e8f0", background: "#f8fafc" }}>
          <button type="button" style={{ background: "#f1f5f9", border: "1.5px solid #cbd5e1", color: "#64748b", borderRadius: 8, padding: "6px 16px", fontSize: "0.83rem", fontWeight: 600, cursor: "pointer" }} onClick={() => setActiveModal(null)} disabled={isCreating}>Cancel</button>
          <button type="button" disabled={isCreating} style={{ background: "linear-gradient(135deg, #0d9488, #14b8a6)", border: "none", color: "#fff", borderRadius: 8, padding: "7px 18px", fontSize: "0.83rem", fontWeight: 700, cursor: isCreating ? "wait" : "pointer", display: "inline-flex", alignItems: "center", gap: 7, boxShadow: "0 3px 10px #0d948833" }} onClick={handleCreate}>
            {isCreating ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> Creating…</> : <><i className="bi bi-person-check-fill" /> Create Account</>}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Credential Reveal Modal ─────────────────────────────────────────── */}
      {activeModal?.kind === "credential" && (
        <Modal
          show
          onHide={() => setActiveModal(null)}
          centered
          backdrop="static"
        >
          <Modal.Header>
            <Modal.Title>
              <i className="bi bi-check-circle-fill me-2 text-success"></i>
              Account Created
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="alert alert-warning d-flex align-items-start gap-2 mb-4">
              <i className="bi bi-exclamation-triangle-fill flex-shrink-0 mt-1"></i>
              <span>
                <strong>Copy these credentials now.</strong> The password will not be shown again once you close this dialog.
              </span>
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold small text-muted">Client Name</label>
              <div className="form-control bg-light">{activeModal.client.name}</div>
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold small text-muted">Login Email</label>
              <div className="form-control bg-light">{activeModal.client.email}</div>
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold small text-muted">Password</label>
              <div className="input-group">
                <div className="form-control bg-light font-monospace">{activeModal.password}</div>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  title="Copy password"
                  onClick={() => navigator.clipboard.writeText(activeModal.password)}
                >
                  <i className="bi bi-clipboard"></i>
                </button>
              </div>
            </div>
            <div className="mb-1">
              <label className="form-label fw-semibold small text-muted">Client Portal URL</label>
              <div className="form-control bg-light font-monospace">
                {window.location.origin}/client/login
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2" onClick={() => setActiveModal(null)}>
              <i className="bi bi-check2"></i>Done — I&apos;ve saved the credentials
            </button>
          </Modal.Footer>
        </Modal>
      )}

      {/* ── Edit Client Modal ────────────────────────────────────────────────── */}
      <Modal
        show={activeModal?.kind === "edit"}
        onHide={() => setActiveModal(null)}
        centered
      >
        <Modal.Header closeButton closeVariant="white" style={{ background: "linear-gradient(135deg, #78350f, #d97706, #f59e0b)", border: "none", padding: "10px 20px" }}>
          <Modal.Title style={{ color: "#fff", fontWeight: 700, fontSize: "0.92rem", display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 7, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className="bi bi-pencil-square" style={{ color: "#fff", fontSize: 13 }} />
            </div>
            Edit Client
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <div className="alert alert-danger py-2 mb-3">{formError}</div>}
          {editForm && (
            <div className="row g-3 align-items-end">
              <div className="col-12">
                <div style={{ borderLeft: "3px solid #6366f1", paddingLeft: 10 }}>
                  <label className="form-label mb-1 fw-semibold" style={{ fontSize: "0.84rem", color: "#334155" }}>Full Name <span className="text-danger">*</span></label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text" style={{ background: "#eef2ff", borderColor: "#c7d2fe", color: "#6366f1" }}><i className="bi bi-person-fill" style={{ fontSize: 12 }} /></span>
                    <input className="form-control form-control-sm" style={{ borderColor: "#c7d2fe", boxShadow: "none" }} title="Client full name" value={editForm.name} onChange={(e) => handleEditField("name", e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div style={{ borderLeft: "3px solid #10b981", paddingLeft: 10 }}>
                  <label className="form-label mb-1 fw-semibold" style={{ fontSize: "0.84rem", color: "#334155" }}>Phone</label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text" style={{ background: "#d1fae5", borderColor: "#a7f3d0", color: "#059669" }}><i className="bi bi-telephone-fill" style={{ fontSize: 12 }} /></span>
                    <input type="tel" className="form-control form-control-sm" style={{ borderColor: "#a7f3d0", boxShadow: "none" }} title="Client phone" value={editForm.phone} onChange={(e) => handleEditField("phone", e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div style={{ borderLeft: "3px solid #0ea5e9", paddingLeft: 10 }}>
                  <label className="form-label mb-1 fw-semibold" style={{ fontSize: "0.84rem", color: "#334155" }}>City</label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text" style={{ background: "#e0f2fe", borderColor: "#bae6fd", color: "#0ea5e9" }}><i className="bi bi-geo-alt-fill" style={{ fontSize: 12 }} /></span>
                    <input className="form-control form-control-sm" style={{ borderColor: "#bae6fd", boxShadow: "none" }} title="Client city" value={editForm.city} onChange={(e) => handleEditField("city", e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="col-12">
                <div className="form-check form-switch">
                  <input type="checkbox" id="clientIsActive" className="form-check-input" role="switch" checked={editForm.isActive} onChange={(e) => handleEditField("isActive", e.target.checked)} />
                  <label className="form-check-label fw-semibold" htmlFor="clientIsActive">Account Active</label>
                  <div className="form-text">Inactive clients cannot log in to their portal.</div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1.5px dashed #e2e8f0", background: "#f8fafc" }}>
          <button type="button" style={{ background: "#f1f5f9", border: "1.5px solid #cbd5e1", color: "#64748b", borderRadius: 8, padding: "6px 16px", fontSize: "0.83rem", fontWeight: 600, cursor: "pointer" }} onClick={() => setActiveModal(null)} disabled={isUpdating}>Cancel</button>
          <button type="button" disabled={isUpdating} style={{ background: "linear-gradient(135deg, #d97706, #f59e0b)", border: "none", color: "#fff", borderRadius: 8, padding: "7px 18px", fontSize: "0.83rem", fontWeight: 700, cursor: isUpdating ? "wait" : "pointer", display: "inline-flex", alignItems: "center", gap: 7, boxShadow: "0 3px 10px #d9770633" }} onClick={handleSaveEdit}>
            {isUpdating ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> Saving…</> : <><i className="bi bi-check2-square" /> Save Changes</>}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Permissions Modal ────────────────────────────────────────────────── */}
      <Modal
        show={activeModal?.kind === "permissions"}
        onHide={() => setActiveModal(null)}
        centered
        size="lg"
      >
        {activeModal?.kind === "permissions" && (
          <PermissionsPanel
            client={activeModal.client}
            onClose={() => setActiveModal(null)}
            onSaved={(msg) => {
              setActionMessage(msg);
              setActiveModal(null);
            }}
          />
        )}
      </Modal>

      {/* ── View Assigned Projects Modal ─────────────────────────────────────── */}
      <Modal
        show={activeModal?.kind === "projects"}
        onHide={() => setActiveModal(null)}
        centered
        size="lg"
      >
        {activeModal?.kind === "projects" && (
          <ClientProjectsPanel
            client={activeModal.client}
            onClose={() => setActiveModal(null)}
          />
        )}
      </Modal>
    </section>
  );
};

export default ClientManager;
